// Whale Tracker — two sections:
// 1. Automated Whale Alert feed (large cross-chain transfers > $500K)
// 2. Watched Wallets (user-curated addresses, polled via Etherscan)

import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { getWorkerBase } from '../worker';
import { ETHERSCAN_KEY, WHALE_ALERT_KEY } from '../worker';

const LS_WALLETS = 'meridian_watched_wallets';

interface WhaleTx {
  id: string; blockchain: string; symbol: string;
  amount: number; amountUsd: number;
  from: string; fromType: string; to: string; toType: string;
  hash: string; ts: string; txType: string;
}
interface WhaleFeedResp { transactions: WhaleTx[]; count: number; cachedAt: string; }
interface WatchedWallet  { address: string; label: string; }

const CHAIN_EXPLORER: Record<string, string> = {
  ethereum: 'https://etherscan.io/tx/',
  bitcoin:  'https://blockstream.info/tx/',
  ripple:   'https://xrpscan.com/tx/',
  tron:     'https://tronscan.org/#/transaction/',
  solana:   'https://solscan.io/tx/',
};

function chainColor(chain: string): string {
  const c: Record<string, string> = { ethereum: '#627eea', bitcoin: '#f7931a', ripple: '#00aae4', tron: '#eb0029', solana: '#9945ff', bnb: '#f3ba2f' };
  return c[chain] ?? 'var(--text3)';
}

function ownerBadge(label: string, type: string): string {
  const col = type === 'exchange' ? 'var(--warn)' : type === 'unknown' ? 'var(--text3)' : 'var(--accent)';
  return `<span style="color:${col};font-size:11px;font-family:var(--mono);">${esc(label)}</span>`;
}

function fmtUsd(n: number): string {
  if (n >= 1e9)  return `$${(n/1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3)  return `$${(n/1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function shortAddr(a: string): string { return a ? a.slice(0,6) + '…' + a.slice(-4) : '?'; }

export class WhaleTrackerTab implements Tab {
  readonly id     = 'whale-tracker';
  private wallets: WatchedWallet[] = [];
  private activeSection: 'feed' | 'wallets' = 'feed';
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.wallets = this.loadWallets();

    this.el('wt-tab-feed').addEventListener('click',    () => this.switchSection('feed'));
    this.el('wt-tab-wallets').addEventListener('click', () => this.switchSection('wallets'));
    this.el('wt-feed-refresh').addEventListener('click', () => this.loadFeed(true));

    // Watched wallets controls
    this.el('wt-add-btn').addEventListener('click', () => this.addWallet());
    (this.el('wt-addr-input') as HTMLInputElement)
      .addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') this.addWallet(); });

    this.el('wt-wallets-body').addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-wt-remove]');
      if (btn) this.removeWallet(btn.dataset.wtRemove ?? '');
    });
  }

  render(): void {
    this.switchSection(this.activeSection);
    if (!this.refreshTimer) {
      this.refreshTimer = setInterval(() => {
        if (this.activeSection === 'feed') this.loadFeed(false);
      }, 30 * 60 * 1000);
    }
  }

  destroy(): void {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
  }

  // ── Section switching ────────────────────────────────────────────────────────

  private switchSection(section: 'feed' | 'wallets'): void {
    this.activeSection = section;
    const isFeed = section === 'feed';
    this.el('wt-tab-feed').classList.toggle('active', isFeed);
    this.el('wt-tab-wallets').classList.toggle('active', !isFeed);
    this.el('wt-feed-panel').style.display    = isFeed ? '' : 'none';
    this.el('wt-wallets-panel').style.display = isFeed ? 'none' : '';
    if (isFeed)    this.loadFeed(false);
    else           this.renderWallets();
  }

  // ── Whale Alert feed ─────────────────────────────────────────────────────────

  private async loadFeed(force: boolean): Promise<void> {
    const waKey = localStorage.getItem(WHALE_ALERT_KEY) ?? '';
    if (!waKey) {
      this.el('wt-feed-body').innerHTML = `
        <div class="empty-state">
          <div style="font-size:26px;margin-bottom:8px;">🐋</div>
          <div style="font-family:var(--mono);font-size:12px;color:var(--warn);">Whale Alert API key required</div>
          <div style="font-size:11px;color:var(--text3);margin-top:6px;line-height:1.6;">
            Free at <a href="https://whale-alert.io" target="_blank" style="color:var(--accent);">whale-alert.io</a><br>
            Add in <kbd style="background:var(--bg3);border:1px solid var(--border);padding:1px 5px;border-radius:2px;font-family:var(--mono);">Ctrl+Shift+,</kbd> → Whale Alert
          </div>
        </div>`;
      return;
    }

    const base = getWorkerBase();
    if (!base) { this.el('wt-feed-body').innerHTML = '<div style="padding:16px;color:var(--red);font-family:var(--mono);font-size:12px;">Worker URL not configured</div>'; return; }

    if (!force) {
      const cached = this.el('wt-feed-body').dataset.loaded;
      if (cached) return;
    }

    this.el('wt-feed-body').innerHTML = '<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Fetching whale activity…</div>';

    try {
      const res  = await fetch(`${base}/api/whale-feed`, { headers: { 'X-Whale-Alert-Key': waKey }, signal: AbortSignal.timeout(12000) });
      const data = await res.json() as WhaleFeedResp & { error?: string };
      if (data.error) throw new Error(data.error);
      this.el('wt-feed-updated').textContent = `Cached ${timeAgo(data.cachedAt)} · ${data.count} txns`;
      this.renderFeed(data.transactions);
      this.el('wt-feed-body').dataset.loaded = '1';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('wt-feed-body').innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderFeed(txs: WhaleTx[]): void {
    if (!txs.length) {
      this.el('wt-feed-body').innerHTML = '<div class="empty-state">No large transfers in the last hour.</div>';
      return;
    }

    this.el('wt-feed-body').innerHTML = txs.map(tx => {
      const chainCol = chainColor(tx.blockchain);
      const explorer = CHAIN_EXPLORER[tx.blockchain] ?? 'https://blockchair.com/search?q=';
      const txLink   = tx.hash ? `${explorer}${esc(tx.hash)}` : '#';
      const arrow    = tx.fromType === 'exchange' ? '↗ Exchange out' : tx.toType === 'exchange' ? '↘ Exchange in' : '→ Transfer';
      const arrowCol = tx.toType === 'exchange' ? 'var(--red)' : tx.fromType === 'exchange' ? 'var(--green)' : 'var(--text3)';

      return `
        <a href="${esc(txLink)}" target="_blank" rel="noopener"
          style="display:flex;gap:12px;align-items:flex-start;padding:12px 14px;background:var(--panel);
                 border:1px solid var(--border);border-left:3px solid ${chainCol};
                 border-radius:3px;margin-bottom:5px;text-decoration:none;color:inherit;">
          <div style="flex-shrink:0;min-width:48px;text-align:center;">
            <div style="font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(tx.symbol)}</div>
            <div style="font-size:9px;color:${chainCol};text-transform:uppercase;letter-spacing:0.06em;">${esc(tx.blockchain)}</div>
          </div>
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap;">
              <span style="font-family:var(--mono);font-size:14px;font-weight:700;color:var(--text);">${esc(fmtUsd(tx.amountUsd))}</span>
              <span style="font-size:11px;color:var(--text3);">${tx.amount.toLocaleString()} ${esc(tx.symbol)}</span>
              <span style="font-size:10px;color:${arrowCol};font-family:var(--mono);">${arrow}</span>
            </div>
            <div style="font-size:11px;color:var(--text2);">
              ${ownerBadge(tx.from, tx.fromType)} <span style="color:var(--text3);">→</span> ${ownerBadge(tx.to, tx.toType)}
            </div>
          </div>
          <div style="flex-shrink:0;font-size:10px;color:var(--text3);font-family:var(--mono);white-space:nowrap;">${timeAgo(tx.ts)}</div>
        </a>`;
    }).join('');
  }

  // ── Watched Wallets ──────────────────────────────────────────────────────────

  private loadWallets(): WatchedWallet[] {
    try { return JSON.parse(localStorage.getItem(LS_WALLETS) ?? '[]') as WatchedWallet[]; }
    catch { return []; }
  }

  private saveWallets(): void { localStorage.setItem(LS_WALLETS, JSON.stringify(this.wallets)); }

  private addWallet(): void {
    const addr  = (this.el('wt-addr-input')  as HTMLInputElement).value.trim();
    const label = (this.el('wt-label-input') as HTMLInputElement).value.trim() || shortAddr(addr);
    if (!addr.match(/^0x[0-9a-fA-F]{40}$/)) {
      (this.el('wt-addr-input') as HTMLInputElement).style.borderColor = 'var(--red)';
      setTimeout(() => { (this.el('wt-addr-input') as HTMLInputElement).style.borderColor = ''; }, 1500);
      return;
    }
    if (this.wallets.some(w => w.address.toLowerCase() === addr.toLowerCase())) return;
    this.wallets.unshift({ address: addr, label });
    this.saveWallets();
    (this.el('wt-addr-input')  as HTMLInputElement).value = '';
    (this.el('wt-label-input') as HTMLInputElement).value = '';
    this.renderWallets();
  }

  private removeWallet(address: string): void {
    this.wallets = this.wallets.filter(w => w.address.toLowerCase() !== address.toLowerCase());
    this.saveWallets();
    this.renderWallets();
  }

  private renderWallets(): void {
    const esKey = localStorage.getItem(ETHERSCAN_KEY) ?? '';

    if (!this.wallets.length) {
      this.el('wt-wallets-body').innerHTML = `
        <div class="empty-state">
          <div style="font-size:24px;margin-bottom:8px;">👀</div>
          <div style="font-size:12px;">Add wallet addresses to monitor above</div>
          <div style="font-size:11px;margin-top:4px;">Smart money, insiders, DAOs, exchange wallets…</div>
        </div>`;
      return;
    }

    this.el('wt-wallets-body').innerHTML = this.wallets.map(w => `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:3px;padding:12px 14px;margin-bottom:6px;display:flex;gap:10px;align-items:center;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:var(--text);margin-bottom:2px;">${esc(w.label)}</div>
          <a href="https://etherscan.io/address/${esc(w.address)}" target="_blank" rel="noopener"
            style="font-family:var(--mono);font-size:10px;color:var(--text3);">${esc(w.address)}</a>
        </div>
        <a href="https://etherscan.io/address/${esc(w.address)}" target="_blank" rel="noopener"
          style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:4px 8px;border-radius:2px;text-decoration:none;flex-shrink:0;">
          View ↗
        </a>
        <button data-wt-remove="${esc(w.address)}"
          style="background:none;border:none;color:var(--text3);cursor:pointer;font-size:13px;padding:2px 4px;"
          onmouseover="this.style.color='var(--red)'" onmouseout="this.style.color='var(--text3)'">✕</button>
      </div>`).join('');

    if (!esKey) {
      this.el('wt-wallets-body').insertAdjacentHTML('afterbegin', `
        <div style="padding:10px 12px;background:rgba(255,193,7,0.08);border:1px solid rgba(255,193,7,0.2);border-radius:3px;margin-bottom:10px;font-size:11px;color:var(--warn);">
          ⚠ Add Etherscan key in Settings to enable activity tracking for watched wallets.
        </div>`);
    }
  }
}
